import { useMemo } from 'react'

import { useGetTrackedGameLiveEventBlueprintsQuery } from '../../../../api/core'
import { useGetTrackedGameEventsQuery } from '../../../api/internal'
import { useGetTrackedBlueprintsQuery } from '../../../api/markets'

/**
 * The event types used by the events of one game blueprint.
 *
 * The blueprint's evolution says which events belong to it, and the game's event list carries each
 * event's type, so the two together give the types without walking every event. An event that never
 * ran is missing from the evolution, so the odd cases the Blueprint Families report for this
 * blueprint are added too; they carry the blueprint whether the event ran or not.
 */
export const useGameBlueprintEventTypes = (trackedGameId?: string, blueprintId?: string) => {
  const eventsQuery = useGetTrackedGameEventsQuery(trackedGameId as string, { skip: !trackedGameId })
  const evolutionQuery = useGetTrackedGameLiveEventBlueprintsQuery({ blueprintId: blueprintId as string, type: 'all' }, { skip: !blueprintId })
  const blueprintFamiliesQuery = useGetTrackedBlueprintsQuery(undefined, { skip: !blueprintId })

  const typeIds = useMemo(() => {
    if (!eventsQuery.data || !evolutionQuery.data) return []
    const eventIds = new Set(evolutionQuery.data.evolution.map((entry) => entry.eventId))
    const evolutionTypes = eventsQuery.data.filter((event) => event.id && eventIds.has(event.id)).map((event) => event.typeId)
    const oddCaseTypes = (blueprintFamiliesQuery.data || []).flatMap((blueprintFamily) =>
      (blueprintFamily.unacceptedEventTypes || [])
        .filter((eventType) => eventType.events.some((event) => event.blueprintId === blueprintId))
        .map((eventType) => eventType.typeId)
    )
    return Array.from(new Set([...evolutionTypes, ...oddCaseTypes].filter((typeId): typeId is string => Boolean(typeId))))
  }, [blueprintId, eventsQuery.data, evolutionQuery.data, blueprintFamiliesQuery.data])

  return { typeIds, isLoading: eventsQuery.isFetching || evolutionQuery.isFetching || blueprintFamiliesQuery.isFetching }
}
